<template>
  <div>
    <div id="noti"></div>
    <s-dialog
      :visible.sync="dialogVisible"
      :close-on-click-modal="false"
      size="large"
      :custom-class="dialogCustomClass"
      :title="$i18n('Notification Content')"
    >
      <p class="noti-title">{{ notiMessage?.notification_title }}</p>
      <p class="noti-content" v-if="!!notiMessage?.notification_content">
        {{ notiMessage?.notification_content }}
      </p>
      <div class="noti-link" v-if="notiMessage?.notification_urls?.length">
        <p>{{ $i18n('URL') }}:</p>
        <ul>
          <li
            v-for="value in notiMessage?.notification_urls"
            :key="value.url_link"
          >
            <a :href="value.url_link" target="_blank">{{ value.url_title }}</a>
          </li>
        </ul>
      </div>
      <div
        class="noti-attachment"
        v-if="notiMessage?.notification_attachments?.length"
      >
        <p>{{ $i18n('Attachment') }}:</p>
        <div class="noti-attachment-list">
          <a
            class="noti-attachment-item"
            v-for="value in notiMessage?.notification_attachments"
            :href="`${host}${value.attachment_link}`"
            :key="value.attachment_link"
            target="_blank"
          >
            <s-icon-attachment-outline
              class="noti-icon-attachment"
              color="#959BA4"
            />
            <span>{{ value.attachment_title }}</span>
            <s-icon-download-outline
              class="noti-icon-download"
              color="#959BA4"
            />
          </a>
        </div>
      </div>
      <div slot="footer" class="noti-dialog-footer">
        <template v-if="showWorkTicketActions">
          <s-button @click="closeDialog">
            {{ $i18n('Close') }}
          </s-button>
          <s-button
            type="primary"
            :disabled="!workTicketId"
            @click="viewWorkTicket"
          >
            {{ $i18n('View the ticket') }}
          </s-button>
        </template>
      </div>
    </s-dialog>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from 'vue-property-decorator';
import { NotiFirebase } from '@ssc-fe-common/noti-firebase';

import NotificationSoundPath from '@/assets/audio/success-notification-sound.mp3';
import { reportEvent } from '@/utils/qms';
import { getApolloConfig } from '@/api/system-config/noti';
import { NotiMessage } from '@/types/system-config/noti';
import { apiConfig } from './config';
import { getPortalConfig } from './portal';

const WORK_TICKET_NOTIFICATION_MATCHER = {
  notificationType: 51, // WMS Notification
  notificationSubType: 60, // Supervisor Notification
};

@Component
export default class NotiWidget extends Vue {
  private schedulerConfig = { enable: false, interval: 0 };

  dialogVisible = false;
  notiMessage: NotiMessage['rawData'] | null = null;
  previousUnreadMessageCount: number | null = null;
  host = window.location.host;

  @Watch('schedulerConfig.enable', { immediate: true })
  onSchedulerConfigEnableChange(enable: boolean) {
    if (!enable) return;
    const noti = NotiFirebase.getInstance();
    noti.manualSwitchScheduler(this.schedulerConfig.interval);
  }

  created() {
    this.initNoti();
    this.listenServiceWorkerMessages();
    this.initPortalConfig();
  }

  beforeDestroy() {
    if (!this.showNoti()) return;
    const noti = NotiFirebase.getInstance();
    noti.unmount();
  }

  private initPortalConfig() {
    const { portalApiPrefix } = getPortalConfig();
    this.host = portalApiPrefix;
  }

  private listenServiceWorkerMessages() {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data.type === 'firebase-error') {
        reportEvent('firebase-error/sw', {
          error: event.data.error || 'unknown error',
        });
      }
    });
  }

  private showNoti(): boolean {
    return true;
  }

  private onView(msg: NotiMessage): void {
    this.notiMessage = msg.rawData || null;
    this.dialogVisible = true;
  }

  get dialogCustomClass(): string {
    return this.showWorkTicketActions
      ? 'noti-dialog noti-dialog--with-actions'
      : 'noti-dialog noti-dialog--without-actions';
  }

  get showWorkTicketActions(): boolean {
    const message = this.notiMessage;
    if (!message) return false;
    return (
      this.normalizeNotificationCode(message.notification_type) ===
        WORK_TICKET_NOTIFICATION_MATCHER.notificationType &&
      this.normalizeNotificationCode(message.notification_sub_type) ===
        WORK_TICKET_NOTIFICATION_MATCHER.notificationSubType
    );
  }

  get workTicketId(): string {
    const message = this.notiMessage;
    if (!message) return '';
    return this.extractTicketIdFromText(message.notification_content);
  }

  private normalizeNotificationCode(value: unknown): number {
    return Number(value);
  }

  private extractTicketIdFromText(text?: string): string {
    if (!text) return '';
    const match = text.match(/Ticket ID\s*[:：]\s*([A-Za-z0-9_-]+)/i);
    return match?.[1] || '';
  }

  private closeDialog(): void {
    this.dialogVisible = false;
  }

  private viewWorkTicket(): void {
    const id = this.workTicketId;
    if (!id) {
      this.$message.error(this.$i18n('Ticket ID is missing'));
      return;
    }
    this.closeDialog();
    this.$router
      .push({
        name: 'workticketmanagement.workticketmanagement.handle',
        params: { id },
      })
      .catch(() => undefined);
  }

  private bindMessageNotificationSound(noti: NotiFirebase): void {
    const getUnreadMessageCount = noti.getUnreadMessageCount.bind(noti);
    noti.getUnreadMessageCount = async () => {
      const count = await getUnreadMessageCount();
      this.handleUnreadMessageCountChange(count);
      return count;
    };
  }

  private handleUnreadMessageCountChange(count: number): void {
    const nextCount = Number(count);
    if (Number.isNaN(nextCount)) return;
    const previousCount = this.previousUnreadMessageCount;
    this.previousUnreadMessageCount = nextCount;
    if (previousCount !== null && nextCount > previousCount) {
      this.playMessageNotificationSound();
    }
  }

  private playMessageNotificationSound(): void {
    if (!this.$store.state.system.enableSoundNotification) return;
    const audio = new Audio(NotificationSoundPath);
    audio.play().catch(() => undefined);
  }

  private async initNoti(): Promise<void> {
    try {
      if (!this.showNoti()) return;
      const apolloConf = await getApolloConfig({
        configKeyList: [
          'application.notiSchedulerSwitch',
          'application.firebaseConfig',
        ],
      });
      if (apolloConf?.data?.list?.[0]?.configValue) {
        this.schedulerConfig = JSON.parse(
          apolloConf?.data?.list?.[0]?.configValue
        );
      }
      const firebaseConfig = JSON.parse(
        apolloConf?.data?.list?.[1]?.configValue ?? '{}'
      );
      const noti = NotiFirebase.getInstance({
        actionConfig: { showView: true, onView: this.onView },
        adapterConfig: { $gt: this.$i18n },
        uiPrefixCls: { prefixCls: 'ssc-react', rcPrefixCls: 'ssc-react-rc' },
        firebaseConfig,
        apiConfig,
      });
      this.bindMessageNotificationSound(noti);
      const notiElement = document.querySelector('#noti');
      if (notiElement) {
        noti.mount(notiElement);
      }
    } catch (e) {
      console.error('init noti error', e);
      reportEvent('firebase-error/init-noti', {
        error: e?.message || 'unknown error',
      });
    }
  }
}
</script>

<style scoped lang="scss">
#noti {
  margin-right: 16px;
}
</style>

<style lang="scss">
.noti-dialog {
  .noti-title {
    font-size: 16px;
    font-weight: 500;
    color: #303844;
  }
  .noti-content {
    margin-top: 8px;
  }
  .noti-link {
    margin-top: 16px;
    p {
      color: #7e8692;
    }
    li {
      margin-top: 4px;
      a {
        display: inline-block;
        max-width: 100%;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        color: #3274f7;
      }
    }
  }
  .noti-attachment {
    margin-top: 16px;
    p {
      color: #7e8692;
      margin-bottom: 4px;
    }
    .noti-attachment-list {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      .noti-attachment-item {
        display: block;
        width: 272px;
        height: 40px;
        border: 1px solid #e2e6ec;
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 12px;
        &:hover {
          background: #f5f6f7;
        }

        .noti-icon-attachment {
          width: 20px;
          margin-right: 8px;
        }

        .noti-icon-download {
          width: 20px;
          margin-left: 8px;
        }

        span {
          color: #303844;
          margin-right: auto;
          flex: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }
  .noti-dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}
.noti-dialog--without-actions {
  .ssc-dialog-body {
    margin: 0 0 24px;
  }
  .ssc-dialog-footer {
    display: none;
  }
}
</style>
